import CalendarHeatmap from 'react-calendar-heatmap';
import React, { Component, ReactNode } from 'react';
import ReactTooltip from 'react-tooltip';
import { Header, Icon, Modal, Popup, Segment } from 'semantic-ui-react';

import { CustomContainer } from '../common';
import { DEFAULT_DAYS_AGO } from '../../constants';
import { formatDatetime } from '../Utils';
import { shiftDate } from './utils';

const MESSAGES = {
  SHOW_RECENT_ACTIVITIES: `
    使用者在過去一定時間內的活動情形
    可以試著點方塊區塊檢視當日的發文或回文喔
  `,
};

const STYLES = {
  QUESTION_ICON: {
    float: 'right' as const,
    cursor: 'pointer',
    color: '#7f8c8d',
  },
};

interface ActivityData {
  date: string | Date;
  count: Record<string, number>;
  [key: string]: any;
}

interface DailyActivitiesDetails {
  data: any[];
  isFetching: boolean;
}

interface ActivitiesProps {
  computeValue: (count: ActivityData['count']) => number;
  dailyActivitiesDetails: DailyActivitiesDetails;
  data: ActivityData[];
  fetchDailyActivites: (userId: string, at: string) => void;
  getTooltipDataAttrs: (value: any) => object;
  isFetching: boolean;
  isMobile: boolean;
  renderActivitiesDetails: (
    data: any[],
    isFetching: boolean,
    isMobile: boolean,
  ) => ReactNode;
  userId: string;
}

interface ActivitiesState {
  showDailyActivitiesDetails: boolean;
  dailyActivitiesAt: string | null;
}

const mapValueToClass = (value: any): string => {
  if (!value || !value.count) {
    return 'color-empty';
  }

  const count = parseInt(String(value.count || 0), 10);

  if (!count) {
    return 'color-empty';
  }

  if (count > 0 && count <= 3) {
    return 'color-github-1';
  }

  if (count > 3 && count <= 9) {
    return 'color-github-2';
  }

  if (count > 9 && count <= 15) {
    return 'color-github-3';
  }

  if (count > 15 && count <= 20) {
    return 'color-github-4';
  }

  if (count > 20 && count <= 25) {
    return 'color-github-5';
  }

  if (count > 25 && count <= 30) {
    return 'color-github-6';
  }

  if (count > 30 && count <= 40) {
    return 'color-github-7';
  }

  return 'color-github-8';
};

const today = new Date();

export default class Activities extends Component<
  ActivitiesProps,
  ActivitiesState
> {
  state: ActivitiesState = {
    showDailyActivitiesDetails: false,
    dailyActivitiesAt: null,
  };

  fetchDailyActivites = (at: string) => {
    this.setState({
      showDailyActivitiesDetails: true,
      dailyActivitiesAt: at,
    });

    this.props.fetchDailyActivites(this.props.userId, at);
  };

  render() {
    const {
      computeValue,
      dailyActivitiesDetails,
      data,
      getTooltipDataAttrs,
      isFetching,
      isMobile,
      renderActivitiesDetails,
    } = this.props;

    const { showDailyActivitiesDetails, dailyActivitiesAt } = this.state;

    const hasRecentActivities = Array.isArray(data) && data.length > 0;

    return (
      <div>
        <CustomContainer>
          <Modal
            open={showDailyActivitiesDetails}
            centered={false}
            dimmer="blurring"
            onClose={() =>
              this.setState({
                showDailyActivitiesDetails: false,
              })
            }
            closeIcon
          >
            <Modal.Header>{dailyActivitiesAt} 的歷史紀錄</Modal.Header>
            <Modal.Content scrolling>
              {renderActivitiesDetails(
                dailyActivitiesDetails.data,
                dailyActivitiesDetails.isFetching,
                isMobile,
              )}
            </Modal.Content>
          </Modal>
          <Header as={isMobile ? 'h3' : 'h2'} attached="top">
            近期活動 ({DEFAULT_DAYS_AGO / 30}
            個月之內)
            <div style={STYLES.QUESTION_ICON}>
              <Popup
                trigger={<Icon name="question" size="small" />}
                content={MESSAGES.SHOW_RECENT_ACTIVITIES}
                position="bottom right"
              />
            </div>
          </Header>
          <Segment attached loading={isFetching}>
            {hasRecentActivities ? (
              <div>
                <CalendarHeatmap
                  startDate={shiftDate(today, -DEFAULT_DAYS_AGO)}
                  endDate={today}
                  values={data.map(({ date, count }) => ({
                    date: formatDatetime(date),
                    count: computeValue(count),
                    countBreakdown: count,
                  }))}
                  classForValue={mapValueToClass}
                  tooltipDataAttrs={value => getTooltipDataAttrs(value)}
                  showMonthLabels
                  showWeekdayLabels={!isMobile}
                  onClick={entry =>
                    entry && this.fetchDailyActivites(entry.date)
                  }
                />
                <ReactTooltip />
              </div>
            ) : (
              <p>沒有任何資料喔...</p>
            )}
          </Segment>
        </CustomContainer>
      </div>
    );
  }
}

